import {ReloadOutlined} from "@ant-design/icons";
import {Breadcrumb, Button, DatePicker, Empty, InputNumber, Space, Table, Tag} from "antd";
import {ColumnsType, TablePaginationConfig} from "antd/es/table";
import {SortOrder} from "antd/es/table/interface";
import dayjs, {Dayjs} from "dayjs";
import React, {useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {Differ, Viewer} from "json-diff-kit";
import "json-diff-kit/dist/viewer.css";
import {AppContextContext, AttributeDefinitionServiceContext, OrganizationServiceContext, SourceUpdatesServiceContext, VulSourceServiceContext} from "../../Contexts";
import AttributeDefinition from "../../domain/AttributeDefinition";
import SourceUpdatesAttributeChangeRow from "../../domain/SourceUpdatesAttributeChangeRow";
import VulSource from "../../domain/VulSource";
import {FormPersister} from "../../sal-ui/FormPersister";
import QueryOptions from "../../sal-ui/QueryOptions";
import {descendingFirstSortDirections} from "../../sal-ui/TableSortDirections";
import {SourceUpdatesAttributeChangeStatsEntry, SourceUpdatesQuery} from "../../service/SourceUpdatesService";
import {DateRangePresetId, resolveDateRangePresetValue} from "../../utils/DateRangePresets";
import {createDateRangePickerPresets, takePendingDatePresetId} from "../../utils/DateRangePresetPicker";
import {formatInteger} from "../../utils/FormatUtils";
import * as globalStyles from "../App.module.css";
import {DocumentTitle} from "../DocumentTitle";
import JsonDifferenceModal from "../common/JsonDifferenceModal";
import {SearchFilter} from "../tablefilter/SearchFilter";
import {FilterItem, FilterItemProps, SelectEditor} from "../tablefilter/TableFilter";
import * as styles from "./SourceUpdatesAttributeChangesPage.module.css";

const differ = new Differ({
    detectCircular: true,
    maxDepth: Infinity,
    showModifications: true,
    arrayDiffMethod: "lcs",
});

const diffViewerProps = {
    indent: 4,
    highlightInlineDiff: true,
    hideUnchangedLines: true,
};

const tablePagination = {
    defaultPageSize: 20,
    pageSizeOptions: ["10", "25", "50", "100", "250", "500", "1000"],
    placement: ["bottomRight", "topRight"],
    showSizeChanger: true,
    hideOnSinglePage: false,
    showTotal: (total: number, range: [number, number]) => `${range[0]}-${range[1]} of ${total} items`,
};

const PERSISTENT_IDENT = "SourceUpdatesAttributeChangesPage";
const DEFAULT_ATTRIBUTE_CHANGES_ORDER_BY = "changedAt desc";

interface SelectOption {
    value: string;
    label: string;
}

interface DistributionOption extends SelectOption {
    color: string;
}

interface AttributeChangeOption extends DistributionOption {
    textColor: string;
}

const changeTypeOptions: AttributeChangeOption[] = [
    {value: "ADDED", label: "Added", color: "#a5d6a7", textColor: "#135200"},
    {value: "UPDATED", label: "Updated", color: "#ffe082", textColor: "#613400"},
    {value: "REMOVED", label: "Removed", color: "#ef9a9a", textColor: "#820014"},
];

const SOURCE_COLOR_PALETTE = [
    "#1677ff",
    "#95de64",
    "#ffc53d",
    "#ff7a45",
    "#b37feb",
    "#5cdbd3",
    "#ff85c0",
    "#ffd666",
    "#69b1ff",
    "#a0d911",
];

interface AttributeChangeFilterValues {
    organizationId?: string;
    dateRangePresetId?: DateRangePresetId;
    attributeNames?: string[];
    sourceIds?: string[];
    changeTypes?: string[];
    similarity?: NumberRangeFilterValue;
    numericDifference?: NumberRangeFilterValue;
    changedRange?: [Dayjs, Dayjs];
}

interface NumberRangeFilterValue {
    min?: number | null;
    max?: number | null;
}

function SourceUpdatesAttributeChangesPage() {
    const appContext = useContext(AppContextContext);
    const attributeDefinitionService = useContext(AttributeDefinitionServiceContext);
    const organizationService = useContext(OrganizationServiceContext);
    const sourceUpdatesService = useContext(SourceUpdatesServiceContext);
    const vulSourceService = useContext(VulSourceServiceContext);
    const location = useLocation();
    const navigate = useNavigate();
    const systemAdmin = appContext.user.isSystemAdmin();
    const defaultFilterValues = useMemo(createDefaultAttributeChangeFilterValues, []);
    const filterFormPersister = useMemo(() => new FormPersister(PERSISTENT_IDENT), []);
    const [rows, setRows] = useState<SourceUpdatesAttributeChangeRow[]>([]);
    const [filterValues, setFilterValues] = useState<AttributeChangeFilterValues>(() =>
        loadAttributeChangeFilterValues(new URLSearchParams(location.search), defaultFilterValues, systemAdmin, filterFormPersister)
    );
    const [organizationOptions, setOrganizationOptions] = useState<{ value: string; label: string }[]>([]);
    const [attributeDefinitions, setAttributeDefinitions] = useState<AttributeDefinition[]>([]);
    const [sources, setSources] = useState<VulSource[]>([]);
    const [differenceModalRow, setDifferenceModalRow] = useState<SourceUpdatesAttributeChangeRow>();
    const [differenceModalOpen, setDifferenceModalOpen] = useState(false);
    const [statsEntries, setStatsEntries] = useState(createEmptyAttributeChangeStatsEntries);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(tablePagination.defaultPageSize);
    const [orderBy, setOrderBy] = useState(DEFAULT_ATTRIBUTE_CHANGES_ORDER_BY);
    const [loading, setLoading] = useState(true);
    const pendingDatePresetIdRef = useRef<DateRangePresetId>();
    const title = "Attribute changes";
    const dateFromStr = filterValues.changedRange?.[0].format("YYYY-MM-DD");
    const dateToStr = filterValues.changedRange?.[1].format("YYYY-MM-DD");
    const sourceUpdatesQuery = useMemo(
        () => buildSourceUpdatesQuery(filterValues, dateFromStr, dateToStr, orderBy, pageSize, (page - 1) * pageSize),
        [filterValues, dateFromStr, dateToStr, orderBy, page, pageSize]
    );
    const hasFilteredRows = totalRows > 0;
    const showEmptyState = !loading && !hasFilteredRows;
    const showTable = loading || hasFilteredRows;
    const stats = useMemo(() => buildAttributeChangeStats(statsEntries), [statsEntries]);
    const attributeOptions = useMemo(() => buildAttributeOptions(attributeDefinitions), [attributeDefinitions]);
    const sourceFilterOptions = useMemo(() => buildSourceOptions(sources), [sources]);
    const sourceStatsOptions = useMemo(() => buildStatsSourceOptions(statsEntries.sources), [statsEntries]);
    const pagination = useMemo<TablePaginationConfig>(() => ({
        ...tablePagination,
        current: page,
        pageSize,
        total: totalRows
    }), [page, pageSize, totalRows]);
    const handleOpenDifference = useCallback((row: SourceUpdatesAttributeChangeRow) => {
        setDifferenceModalRow(row);
        setDifferenceModalOpen(true);
    }, []);
    const handleCloseDifference = useCallback(() => {
        setDifferenceModalOpen(false);
    }, []);
    const handleDifferenceModalOpenChange = useCallback((open: boolean) => {
        if (!open) {
            setDifferenceModalRow(undefined);
        }
    }, []);
    const handleTableChange = useCallback((nextPagination: TablePaginationConfig, _filters: unknown, sorter: any) => {
        const nextPageSize = nextPagination.pageSize ?? pageSize;

        setPageSize(nextPageSize);
        setPage(nextPagination.current ?? 1);
        setOrderBy(orderByFromSorter(sorter));
    }, [pageSize]);
    const columnSortOrder = useCallback((column: string): SortOrder | undefined => {
        const [sortedColumn, direction] = orderBy.split(" ");

        if (sortedColumn !== column) {
            return undefined;
        }

        return direction === "desc" ? "descend" : "ascend";
    }, [orderBy]);
    const columns = useMemo<ColumnsType<SourceUpdatesAttributeChangeRow>>(() => [
        {
            dataIndex: "itemId",
            key: "itemId",
            title: "Item ID",
            width: 100,
            align: "center",
            render: renderItemId,
            sorter: true,
            sortOrder: columnSortOrder("itemId")
        },
        {
            dataIndex: "sourceName",
            key: "sourceName",
            title: "Source",
            width: 150,
            render: (value, row) => <Link to={`/vul-sources/${row.sourceId}`} target="_blank" rel="noopener noreferrer">{value}</Link>,
            sorter: true,
            sortOrder: columnSortOrder("sourceName")
        },
        {
            dataIndex: "changedAt",
            key: "changedAt",
            title: "Changed at",
            width: 150,
            render: value => dayjs(value).format("YYYY-MM-DD HH:mm"),
            sorter: true,
            sortDirections: descendingFirstSortDirections,
            sortOrder: columnSortOrder("changedAt")
        },
        {
            dataIndex: "attributeName",
            key: "attributeName",
            title: "Attribute",
            width: 130,
            sorter: true,
            sortOrder: columnSortOrder("attributeName")
        },
        {
            dataIndex: "changeType",
            key: "changeType",
            title: "Change type",
            width: 120,
            align: "center",
            render: value => renderChangeType(value)
        },
        {
            dataIndex: "normalizedSimilarity",
            key: "normalizedSimilarity",
            title: "Similarity",
            width: 95,
            align: "right",
            render: value => formatSimilarity(value),
            sorter: true,
            sortOrder: columnSortOrder("normalizedSimilarity")
        },
        {
            dataIndex: "numericDifference",
            key: "numericDifference",
            title: "Num. diff",
            width: 120,
            align: "right",
            render: value => formatNumericDifference(value),
            sorter: true,
            sortOrder: columnSortOrder("numericDifference")
        },
        {
            dataIndex: "difference",
            title: "Difference",
            width: 570,
            render: (_value, row) => <AttributeDifferencePreview row={row} onOpen={handleOpenDifference}/>
        },
    ], [columnSortOrder, handleOpenDifference]);

    useEffect(() => {
        reloadAttributeChanges();
    }, [sourceUpdatesQuery]);

    useEffect(() => {
        attributeDefinitionService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setAttributeDefinitions(result.data));

        vulSourceService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setSources(result.data));
    }, [attributeDefinitionService, vulSourceService]);

    useEffect(() => {
        if (!systemAdmin) {
            return;
        }

        organizationService.getList(QueryOptions.newUnlimitedOrderedInstance("name"))
            .then(result => setOrganizationOptions(result.data.map(organization => ({
                value: organization.id!,
                label: organization.name!
            }))));
    }, [organizationService, systemAdmin]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);

        if (!searchParams.has("filter")) {
            return;
        }

        setFilterValues(loadAttributeChangeFilterValues(searchParams, defaultFilterValues, systemAdmin, filterFormPersister));
        navigate(location.pathname, {replace: true});
    }, [defaultFilterValues, filterFormPersister, location.pathname, location.search, navigate, systemAdmin]);

    useEffect(() => {
        filterFormPersister.saveState(filterValues);
    }, [filterFormPersister, filterValues]);

    return (
        <DocumentTitle title={title}>
            <>
                <Breadcrumb className={globalStyles["common__breadcrumb"]}
                            items={[
                                {title: appContext.config?.appName},
                                {title}
                            ]}
                />

                <h1>{title}</h1>

                <AttributeChangesFilter
                    systemAdmin={systemAdmin}
                    organizationOptions={organizationOptions}
                    attributeOptions={attributeOptions}
                    sourceOptions={sourceFilterOptions}
                    values={filterValues}
                    defaultValues={defaultFilterValues}
                    persistentStateIdent={PERSISTENT_IDENT}
                    pendingDatePresetIdRef={pendingDatePresetIdRef}
                    onChange={onFilterChange}
                />

                <div className={globalStyles["common__top-button-bar"]}>
                    <Button className={"btn-seamless"} icon={<ReloadOutlined/>} loading={loading} onClick={reloadAttributeChanges}/>
                </div>

                {showEmptyState && (
                    <Empty description="No attribute changes found for the selected filter." style={{margin: "48px 0"}}/>
                )}

                {showTable && (
                    <>
                        {hasFilteredRows && (
                            <div className={styles.stats}>
                                {renderDistribution(
                                    "Change type",
                                    toDistributionItems(changeTypeOptions, stats.changeTypeCounts),
                                    totalRows
                                )}
                                {renderDistribution(
                                    "Sources",
                                    toDistributionItems(sourceStatsOptions, stats.sourceCounts),
                                    totalRows
                                )}
                            </div>
                        )}

                        <AttributeChangesTable
                            rows={rows}
                            loading={loading}
                            pagination={pagination}
                            columns={columns}
                            onChange={handleTableChange}
                        />
                    </>
                )}

                <JsonDifferenceModal
                    open={differenceModalOpen}
                    title={differenceModalRow ? `Difference: ${differenceModalRow.attributeName}` : "Difference"}
                    oldValue={parseAttributeValue(differenceModalRow?.oldValue)}
                    newValue={parseAttributeValue(differenceModalRow?.newValue)}
                    oldValuePresent={hasAttributeValue(differenceModalRow?.oldValue)}
                    newValuePresent={hasAttributeValue(differenceModalRow?.newValue)}
                    afterOpenChange={handleDifferenceModalOpenChange}
                    onClose={handleCloseDifference}
                />
            </>
        </DocumentTitle>
    );

    function reloadAttributeChanges() {
        setLoading(true);

        sourceUpdatesService.getAttributeChanges(sourceUpdatesQuery)
            .then(result => {
                setRows(result.data);
                setStatsEntries(result.stats);
                setTotalRows(result.total);
            })
            .finally(() => setLoading(false));
    }

    function onFilterChange(values: AttributeChangeFilterValues) {
        setFilterValues(withResolvedDateRangePreset(values, filterValues.changedRange));
        setPage(1);
    }
}

interface AttributeChangeStats {
    sourceCounts: Map<string, number>;
    changeTypeCounts: Map<string, number>;
}

interface AttributeChangeStatsEntries {
    sources: SourceUpdatesAttributeChangeStatsEntry[];
    changeTypes: SourceUpdatesAttributeChangeStatsEntry[];
}

interface AttributeChangesFilterProps {
    systemAdmin: boolean;
    organizationOptions: { value: string; label: string }[];
    attributeOptions: { value: string; label: string }[];
    sourceOptions: SelectOption[];
    values: AttributeChangeFilterValues;
    defaultValues: AttributeChangeFilterValues;
    persistentStateIdent?: string;
    pendingDatePresetIdRef: React.MutableRefObject<DateRangePresetId | undefined>;
    onChange: (values: AttributeChangeFilterValues) => void;
}

interface MultiSelectFilterConfig {
    width: number;
    showSearch?: boolean;
    popupMatchSelectWidth?: boolean | number;
}

interface AttributeChangesTableProps {
    rows: SourceUpdatesAttributeChangeRow[];
    loading: boolean;
    pagination: TablePaginationConfig;
    columns: ColumnsType<SourceUpdatesAttributeChangeRow>;
    onChange: (pagination: TablePaginationConfig, filters: unknown, sorter: any) => void;
}

function AttributeChangesFilter(props: AttributeChangesFilterProps) {
    const renderMultiSelectFilter = (options: { value: string; label: string }[],
                                     config: MultiSelectFilterConfig) => (itemProps: FilterItemProps) => (
        <SelectEditor
            multiple={true}
            showSearch={config.showSearch}
            width={config.width}
            popupMatchSelectWidth={config.popupMatchSelectWidth}
            value={itemProps.value}
            onChange={itemProps.onChange}
            options={options}
        />
    );

    const items: FilterItem[] = [
        {
            name: "changedRange",
            key: "changedRange",
            label: "Changed",
            render: itemProps => (
                <DatePicker.RangePicker
                    className={styles.changedRangePicker}
                    allowClear={false}
                    size="small"
                    value={itemProps.value}
                    onChange={value => {
                        const pendingDatePresetId = takePendingDatePresetId(props.pendingDatePresetIdRef);

                        itemProps.onDraftValuesChange?.({
                            ...itemProps.draftValues,
                            changedRange: normalizeChangedRange(value),
                            dateRangePresetId: pendingDatePresetId
                        });
                    }}
                    presets={createDateRangePickerPresets(
                        itemProps.draftValues?.dateRangePresetId,
                        props.pendingDatePresetIdRef,
                        (dateRangePresetId, changedRange) => itemProps.onDraftValuesChange?.({
                            ...itemProps.draftValues,
                            changedRange,
                            dateRangePresetId
                        })
                    )}
                />
            )
        },
    ];

    if (props.systemAdmin) {
        items.push({
            name: "organizationId",
            key: "organizationId",
            label: "Organization",
            render: itemProps => (
                <SelectEditor
                    multiple={false}
                    showSearch={true}
                    width={160}
                    value={itemProps.value}
                    onChange={itemProps.onChange}
                    options={props.organizationOptions}
                />
            )
        });
    }

    items.push(
        {
            name: "attributeNames",
            key: "attributeNames",
            label: "Attribute",
            render: renderMultiSelectFilter(props.attributeOptions, {
                width: 150,
                showSearch: true
            })
        },
        {
            name: "sourceIds",
            key: "sourceIds",
            label: "Source",
            render: renderMultiSelectFilter(props.sourceOptions, {
                width: 120
            })
        },
        {
            name: "changeTypes",
            key: "changeTypes",
            label: "Change type",
            render: renderMultiSelectFilter(changeTypeOptions, {
                width: 120,
                popupMatchSelectWidth: false
            })
        },
        {
            name: "similarity",
            key: "similarity",
            label: "Similarity",
            render: itemProps => (
                <Space.Compact size="small">
                    <InputNumber
                        min={0}
                        max={100}
                        precision={1}
                        step={0.1}
                        placeholder="Min"
                        value={itemProps.value?.min}
                        onChange={value => itemProps.onChange({...itemProps.value, min: normalizeNumberBoundary(value)})}
                        style={{width: 76}}
                    />
                    <InputNumber
                        min={0}
                        max={100}
                        precision={1}
                        step={0.1}
                        placeholder="Max"
                        value={itemProps.value?.max}
                        onChange={value => itemProps.onChange({...itemProps.value, max: normalizeNumberBoundary(value)})}
                        style={{width: 76}}
                    />
                </Space.Compact>
            )
        },
        {
            name: "numericDifference",
            key: "numericDifference",
            label: "Num. diff",
            render: itemProps => (
                <Space.Compact size="small">
                    <InputNumber
                        precision={3}
                        step={0.1}
                        placeholder="Min"
                        value={itemProps.value?.min}
                        onChange={value => itemProps.onChange({...itemProps.value, min: normalizeNumberBoundary(value)})}
                        style={{width: 68}}
                    />
                    <InputNumber
                        precision={3}
                        step={0.1}
                        placeholder="Max"
                        value={itemProps.value?.max}
                        onChange={value => itemProps.onChange({...itemProps.value, max: normalizeNumberBoundary(value)})}
                        style={{width: 68}}
                    />
                </Space.Compact>
            )
        },
    );

    return (
        <SearchFilter
            className={styles.filters}
            items={items}
            values={props.values}
            defaultValues={props.defaultValues}
            persistentStateIdent={props.persistentStateIdent}
            defaultActiveFilterNames={["changedRange", "attributeNames"]}
            immediateMode={false}
            onChange={props.onChange}
        />
    );
}

const AttributeChangesTable = React.memo(function AttributeChangesTable(props: AttributeChangesTableProps) {
    return (
        <div className={styles.tableWrap}>
            <Table
                rowKey={row => `${row.changeLogId}:${row.attributeName}`}
                loading={props.loading}
                pagination={props.pagination}
                size="small"
                showSorterTooltip={false}
                dataSource={props.rows}
                columns={props.columns}
                onChange={props.onChange}
                scroll={{x: 1450}}
            />
        </div>
    );
});

function createEmptyAttributeChangeStatsEntries(): AttributeChangeStatsEntries {
    return {
        sources: [],
        changeTypes: []
    };
}

function buildAttributeChangeStats(entries: AttributeChangeStatsEntries): AttributeChangeStats {
    return {
        sourceCounts: countMap(entries.sources),
        changeTypeCounts: countMap(entries.changeTypes)
    };
}

function countMap(entries: SourceUpdatesAttributeChangeStatsEntry[]) {
    return new Map(entries.map(entry => [entry.key, entry.count]));
}

function buildSourceOptions(sources: VulSource[]): SelectOption[] {
    return sources
        .slice()
        .sort((a, b) => compareText(a.name, b.name))
        .map(source => ({
            value: source.id ?? "",
            label: source.name ?? ""
        }))
        .filter(option => option.value.length > 0);
}

function buildStatsSourceOptions(entries: SourceUpdatesAttributeChangeStatsEntry[]): DistributionOption[] {
    return entries
        .slice()
        .sort((a, b) => compareText(a.label, b.label))
        .map((entry, index) => ({
            value: entry.key,
            label: entry.label,
            color: SOURCE_COLOR_PALETTE[index % SOURCE_COLOR_PALETTE.length]
        }));
}

function buildAttributeOptions(attributeDefinitions: AttributeDefinition[]) {
    return attributeDefinitions
        .slice()
        .sort((a, b) => compareText(a.name, b.name))
        .map(attributeDefinition => ({
            value: attributeDefinition.name ?? "",
            label: attributeDefinition.name ?? ""
        }))
        .filter(option => option.value.length > 0);
}

function createDefaultAttributeChangeFilterValues(): AttributeChangeFilterValues {
    const dateRangePresetId = "last-7-days";

    return {
        organizationId: undefined,
        dateRangePresetId,
        changedRange: resolveDateRangePresetValue(dateRangePresetId)!,
        attributeNames: [],
        sourceIds: [],
        changeTypes: [],
        similarity: undefined,
        numericDifference: undefined
    };
}

function loadAttributeChangeFilterValues(searchParams: URLSearchParams,
                                         defaults: AttributeChangeFilterValues,
                                         systemAdmin: boolean,
                                         filterFormPersister: FormPersister): AttributeChangeFilterValues {
    const filterValues = searchParams.has("filter")
        ? readFilterValuesFromSearchParams(searchParams, filterFormPersister)
        : filterFormPersister.loadValues();

    return normalizeAttributeChangeFilterValues(filterValues ?? {}, defaults, systemAdmin);
}

function readFilterValuesFromSearchParams(searchParams: URLSearchParams,
                                          filterFormPersister: FormPersister): Partial<AttributeChangeFilterValues> | undefined {
    const filter = searchParams.get("filter");

    if (!filter) {
        return undefined;
    }

    try {
        return filterFormPersister.loadValues(FormPersister.decodeUrlState(filter));
    } catch {
        return undefined;
    }
}

function normalizeAttributeChangeFilterValues(values: Partial<AttributeChangeFilterValues>,
                                              defaults: AttributeChangeFilterValues,
                                              systemAdmin: boolean): AttributeChangeFilterValues {
    const presetId = normalizeDateRangePresetId(values.dateRangePresetId);
    const presetRange = resolveDateRangePresetValue(presetId);

    return {
        organizationId: systemAdmin ? normalizeString(values.organizationId) : undefined,
        dateRangePresetId: presetRange ? presetId : undefined,
        changedRange: presetRange ?? normalizeDateRangeFilter(values.changedRange) ?? defaults.changedRange,
        attributeNames: normalizeStringList(values.attributeNames),
        sourceIds: normalizeStringList(values.sourceIds),
        changeTypes: normalizeStringList(values.changeTypes),
        similarity: normalizeNumberRangeFilter(values.similarity),
        numericDifference: normalizeNumberRangeFilter(values.numericDifference)
    };
}

function normalizeChangedRange(value: null | [Dayjs | null, Dayjs | null]) {
    if (!value?.[0] || !value?.[1]) {
        return undefined;
    }

    return [value[0], value[1]] as [Dayjs, Dayjs];
}

function withResolvedDateRangePreset(values: AttributeChangeFilterValues,
                                     previousChangedRange?: [Dayjs, Dayjs]): AttributeChangeFilterValues {
    if (!values.changedRange) {
        return {...values, dateRangePresetId: undefined};
    }

    if (values.dateRangePresetId !== undefined) {
        return values;
    }

    if (previousChangedRange !== undefined && sameDateRange(values.changedRange, previousChangedRange)) {
        return values;
    }

    return {
        ...values,
        dateRangePresetId: undefined
    };
}

function sameDateRange(a: [Dayjs, Dayjs], b: [Dayjs, Dayjs]) {
    return a[0].format("YYYY-MM-DD") === b[0].format("YYYY-MM-DD")
        && a[1].format("YYYY-MM-DD") === b[1].format("YYYY-MM-DD");
}

function normalizeDateRangePresetId(value: unknown): DateRangePresetId | undefined {
    return resolveDateRangePresetValue(value) ? value as DateRangePresetId : undefined;
}

function normalizeDateRangeFilter(value: unknown): [Dayjs, Dayjs] | undefined {
    if (!Array.isArray(value) || value.length < 2) {
        return undefined;
    }

    const from = parseOptionalFilterDate(value[0]);
    const to = parseOptionalFilterDate(value[1]);

    if (!from || !to) {
        return undefined;
    }

    return [from, to];
}

function normalizeNumberBoundary(value: number | string | null) {
    if (typeof value === "number") {
        return value;
    }

    if (typeof value === "string" && value.trim().length > 0) {
        const parsed = Number(value);

        return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
}

function normalizeNumberRangeFilter(value: unknown): NumberRangeFilterValue | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    return {
        min: normalizeNumberBoundary((value as NumberRangeFilterValue).min ?? null),
        max: normalizeNumberBoundary((value as NumberRangeFilterValue).max ?? null)
    };
}

function parseOptionalFilterDate(value: unknown) {
    if (dayjs.isDayjs(value)) {
        return value.isValid() ? value : undefined;
    }

    if (!value || typeof value !== "string") {
        return undefined;
    }

    const date = dayjs(value);

    if (!date.isValid()) {
        return undefined;
    }

    return date;
}

function normalizeString(value: unknown) {
    return typeof value === "string" && value.length > 0 ? value : undefined;
}

function normalizeStringList(value: unknown) {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function buildSourceUpdatesQuery(filterValues: AttributeChangeFilterValues,
                                 dateFrom: string | undefined,
                                 dateTo: string | undefined,
                                 orderBy: string,
                                 top: number,
                                 skip: number): SourceUpdatesQuery {
    return {
        dateFrom,
        dateTo,
        organizationId: filterValues.organizationId,
        attributeNames: filterValues.attributeNames,
        sourceIds: filterValues.sourceIds,
        changeTypes: filterValues.changeTypes,
        similarityMin: filterValues.similarity?.min,
        similarityMax: filterValues.similarity?.max,
        numericDifferenceMin: filterValues.numericDifference?.min,
        numericDifferenceMax: filterValues.numericDifference?.max,
        orderBy,
        top,
        skip
    };
}

function orderByFromSorter(sorter: any) {
    const activeSorter = Array.isArray(sorter) ? sorter.find(item => item.order) : sorter;

    if (!activeSorter?.order || !activeSorter?.columnKey) {
        return DEFAULT_ATTRIBUTE_CHANGES_ORDER_BY;
    }

    return `${activeSorter.columnKey} ${activeSorter.order === "descend" ? "desc" : "asc"}`;
}

function renderItemId(value: string) {
    return <Link to={`/vul-source-items/${value}`}>
        <span title={value}>{shortItemId(value)}</span>
    </Link>;
}

interface AttributeDifferencePreviewProps {
    row: SourceUpdatesAttributeChangeRow;
    onOpen: (row: SourceUpdatesAttributeChangeRow) => void;
}

const AttributeDifferencePreview = React.memo(function AttributeDifferencePreview(props: AttributeDifferencePreviewProps) {
    const contentRef = useRef<HTMLDivElement>(null);
    const [truncated, setTruncated] = useState(false);

    useLayoutEffect(() => {
        const updateTruncated = () => {
            const content = contentRef.current;

            if (!content) {
                setTruncated(false);

                return;
            }

            setTruncated(
                content.scrollHeight > content.clientHeight + 1
                || content.scrollWidth > content.clientWidth + 1
            );
        };

        updateTruncated();

        const resizeObserver = typeof ResizeObserver !== "undefined"
            ? new ResizeObserver(updateTruncated)
            : undefined;

        if (contentRef.current && resizeObserver) {
            resizeObserver.observe(contentRef.current);
        }

        window.addEventListener("resize", updateTruncated);

        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener("resize", updateTruncated);
        };
    }, [props.row]);

    return (
        <div className={styles.differencePreview}>
            <div ref={contentRef} className={styles.differencePreviewContent}>
                {renderAttributeDifferencePreview(props.row)}
            </div>
            {truncated && (
                <Button
                    type="link"
                    size="small"
                    className={styles.differencePreviewAction}
                    onClick={() => props.onOpen(props.row)}
                >
                    <i>« show full difference »</i>
                </Button>
            )}
        </div>
    );
});

function renderAttributeDifferencePreview(row: SourceUpdatesAttributeChangeRow) {
    return renderAttributeDifference(row, styles.diffPreview, styles.singleValuePreview);
}

function renderAttributeDifference(row: SourceUpdatesAttributeChangeRow, diffClassName: string, singleValueClassName: string) {
    const hasOldValue = hasAttributeValue(row.oldValue);
    const hasNewValue = hasAttributeValue(row.newValue);

    if (!hasOldValue && hasNewValue) {
        return renderSingleAttributeValue(row.newValue, singleValueClassName, styles.addedValue);
    }

    if (hasOldValue && !hasNewValue) {
        return renderSingleAttributeValue(row.oldValue, singleValueClassName, styles.removedValue);
    }

    const oldValue = parseAttributeValue(row.oldValue);
    const newValue = parseAttributeValue(row.newValue);

    return (
        <Viewer
            className={diffClassName}
            diff={differ.diff(oldValue, newValue)}
            {...diffViewerProps}
        />
    );
}

function renderSingleAttributeValue(value: string | null | undefined, valueClassName: string, stateClassName: string) {
    return <pre className={`${valueClassName} ${stateClassName}`}>{formatAttributeValue(value)}</pre>;
}

interface DistributionItem {
    key: string;
    label: string;
    count: number;
    color: string;
}

function toDistributionItems(options: DistributionOption[], counts: Map<string, number>): DistributionItem[] {
    return options.map(option => ({
        key: option.value,
        label: option.label,
        count: counts.get(option.value) ?? 0,
        color: option.color
    }));
}

function renderDistribution(title: string, items: DistributionItem[], total: number) {
    const visibleItems = items.filter(item => item.count > 0);

    return (
        <div className={styles.distribution}>
            <div className={styles.distributionHeader}>
                <span className={styles.distributionTitle}>{title}</span>
            </div>
            <div className={styles.distributionBody}>
                <div
                    className={styles.distributionPie}
                    style={{background: distributionPieBackground(visibleItems, total)}}
                    title={visibleItems.map(item => `${item.label}: ${formatInteger(item.count)} (${formatShare(item.count, total)})`).join("\n")}
                    aria-label={title}
                />
                <div className={styles.distributionLegend}>
                    {items.map(item => (
                        <span key={item.key} className={styles.distributionLegendItem}>
                            <span className={styles.distributionSwatch} style={{backgroundColor: item.color}}/>
                            <span>{item.label}</span>
                            <strong>{formatInteger(item.count)}</strong>
                            <span>{formatShare(item.count, total)}</span>
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

function distributionPieBackground(items: DistributionItem[], total: number) {
    if (items.length === 0 || total === 0) {
        return "#f0f0f0";
    }

    let currentPercent = 0;

    const segments = items.map(item => {
        const startPercent = currentPercent;

        currentPercent += item.count * 100 / total;

        return `${item.color} ${startPercent}% ${currentPercent}%`;
    });

    return `conic-gradient(${segments.join(", ")})`;
}

function formatAttributeValue(value: string | null | undefined) {
    const parsedValue = parseAttributeValue(value);

    return JSON.stringify(parsedValue, null, 4);
}

function parseAttributeValue(value?: string | null) {
    if (!hasAttributeValue(value)) {
        return null;
    }

    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

function hasAttributeValue(value?: string | null) {
    return value !== null && value !== undefined;
}

function renderChangeType(changeType: string) {
    const option = findAttributeChangeOption(changeTypeOptions, changeType);

    if (!option) {
        return <Tag>{changeType}</Tag>;
    }

    return renderAttributeChangeTag(option);
}

function renderAttributeChangeTag(option: AttributeChangeOption) {
    return (
        <Tag style={{
            backgroundColor: option.color,
            borderColor: option.color,
            color: option.textColor
        }}>
            {option.label}
        </Tag>
    );
}

function formatSimilarity(value?: number | null) {
    if (value === null || value === undefined) {
        return "";
    }

    return `${(value * 100).toFixed(1)}%`;
}

function formatNumericDifference(value?: number | null) {
    if (value === null || value === undefined) {
        return "";
    }

    const formattedValue = new Intl.NumberFormat(undefined, {maximumFractionDigits: 4}).format(Math.abs(value));

    if (value > 0) {
        return `+${formattedValue}`;
    }

    if (value < 0) {
        return `-${formattedValue}`;
    }

    return "0";
}

function formatShare(count: number, total: number) {
    if (total === 0) {
        return "0.0%";
    }

    return `${(count * 100 / total).toFixed(1)}%`;
}

function findAttributeChangeOption(options: AttributeChangeOption[], value: string) {
    return options.find(option => option.value === value);
}

function compareText(a?: string, b?: string) {
    return (a ?? "").localeCompare(b ?? "");
}

function shortItemId(value: string) {
    return value.substring(0, 2) + "..." + value.substring(value.length - 2);
}

export default SourceUpdatesAttributeChangesPage;
